import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { StatusPill } from '../components/StatusPill';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  History, 
  Search, 
  Loader2,
  FileText,
  Clock,
  User,
  Monitor,
  Shield,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

export default function Audit() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await api.get('/audit/logs');
      setLogs(response.data);
    } catch (error) {
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}min ${secs}s` : `${secs}s`;
  };

  const formatEmissionDate = (value) => {
    if (!value) return '-';
    const [year, month, day] = String(value).split('-');
    if (year && month && day) return `${day}/${month}/${year}`;
    return value;
  };

  const filteredLogs = logs.filter(log => 
    log.invoice_number?.includes(searchTerm) ||
    log.invoice_key?.includes(searchTerm) ||
    log.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openDetails = async (log) => {
    setShowModal(true);
    setDetailsLoading(true);
    setSelectedLog(log);

    try {
      const response = await api.get(`/audit/logs/${encodeURIComponent(log.invoice_number)}`);
      setSelectedLog(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes da conferência');
      setShowModal(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 md:ml-64 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a365d]" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-6 md:p-8 lg:p-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-[#1a365d]" data-testid="audit-title">
            <History className="inline mr-3" size={32} />
            Auditoria e Logs
          </h1>
          <p className="text-slate-500 mt-1">
            Histórico completo de conferências realizadas
          </p>
        </div>

        {/* Search */}
        <div className="mb-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por número da NF, chave ou usuário..."
            className="pl-10"
            data-testid="search-audit-input"
          />
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">NF</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Emissão</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Usuário</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Tempo</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Divergência</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Finalizado em</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                      Nenhum log encontrado
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-mono font-medium text-slate-800">{log.invoice_number}</p>
                        <p className="text-xs text-slate-500 font-mono truncate max-w-[150px]">{log.invoice_key}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatEmissionDate(log.emission_date)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{log.user_name}</p>
                        <p className="text-xs text-slate-500 capitalize">{log.user_role?.replace('_', ' ')}</p>
                      </td>
                      <td className="px-4 py-3 text-center font-mono text-slate-600">
                        {formatTime(log.total_time_seconds)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {log.had_excess_attempt || log.had_gestor_unlock ? (
                          <span className="inline-flex items-center gap-1 text-amber-600">
                            <AlertTriangle size={16} />
                            Sim
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <CheckCircle2 size={16} />
                            Não
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">
                        {log.end_time ? new Date(log.end_time).toLocaleString('pt-BR') : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDetails(log)}
                          data-testid={`view-log-${log.id}`}
                        >
                          Detalhes
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Log Details Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                Detalhes da Conferência
              </DialogTitle>
              <DialogDescription>
                NF-e {selectedLog?.invoice_number}
              </DialogDescription>
            </DialogHeader>
            
            {detailsLoading ? (
              <div className="py-12 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#1a365d]" />
              </div>
            ) : selectedLog && (
              <div className="space-y-6">
                {/* Invoice Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="text-[#1a365d]" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">Número da NF</p>
                      <p className="font-mono font-medium">{selectedLog.invoice_number}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="text-[#1a365d]" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">Tempo Total</p>
                      <p className="font-medium">{formatTime(selectedLog.total_time_seconds)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <User className="text-[#1a365d]" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">Usuário</p>
                      <p className="font-medium">{selectedLog.user_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Monitor className="text-[#1a365d]" size={20} />
                    <div>
                      <p className="text-xs text-slate-500">Máquina/IP</p>
                      <p className="font-medium">{selectedLog.machine_ip}</p>
                    </div>
                  </div>
                </div>

                {/* Chave */}
                <div>
                  <Label className="text-slate-500">Chave da NF-e</Label>
                  <p className="font-mono text-sm bg-slate-100 p-2 rounded mt-1 break-all">
                    {selectedLog.invoice_key}
                  </p>
                </div>

                {/* Hash */}
                <div>
                  <Label className="text-slate-500">Hash de Integridade</Label>
                  <p className="font-mono text-xs bg-slate-100 p-2 rounded mt-1 break-all">
                    {selectedLog.conference_hash}
                  </p>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Início</Label>
                    <p className="font-medium">
                      {new Date(selectedLog.start_time).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Término</Label>
                    <p className="font-medium">
                      {selectedLog.end_time ? new Date(selectedLog.end_time).toLocaleString('pt-BR') : '-'}
                    </p>
                  </div>
                </div>

                {/* Divergences */}
                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-3 rounded-lg ${selectedLog.had_excess_attempt ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                    <p className="text-sm font-medium flex items-center gap-2">
                      <AlertTriangle size={16} />
                      Tentativa de Excesso
                    </p>
                    <p className="text-lg font-bold mt-1">
                      {selectedLog.had_excess_attempt ? 'Sim' : 'Não'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${selectedLog.had_gestor_unlock ? 'bg-blue-50 border border-blue-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Shield size={16} />
                      Desbloqueio de Gestor
                    </p>
                    <p className="text-lg font-bold mt-1">
                      {selectedLog.had_gestor_unlock ? 'Sim' : 'Não'}
                    </p>
                  </div>
                </div>

                {/* Products */}
                <div>
                  <Label className="text-slate-500">Produtos da NF-e</Label>
                  <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Produto</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Quantidade</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Conferida</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {selectedLog.items?.length ? (
                            selectedLog.items.map((item, index) => (
                              <tr key={`${item.ean || item.internal_code || 'item'}-${index}`}>
                                <td className="px-4 py-3">
                                  <p className="font-medium text-slate-800">{item.description || '-'}</p>
                                  <p className="text-xs text-slate-500 font-mono">
                                    {item.ean || item.internal_code || '-'}
                                  </p>
                                </td>
                                <td className="px-4 py-3 text-center font-mono text-slate-700">
                                  {item.expected_quantity ?? 0}
                                </td>
                                <td className="px-4 py-3 text-center font-mono font-semibold text-[#1a365d]">
                                  {item.checked_quantity ?? 0}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={3} className="px-4 py-6 text-center text-slate-500">
                                Nenhum item encontrado para esta conferência
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
